import { Button, Form, Input } from "@light-technology/components";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import logo from "../../assets/logo.svg";
import { api } from "../../state/api";
import { useDocumentTitle } from "../../utilities/useDocumentTitle";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { emailFieldRules } from "../../utilities/validation";

type LoginForm = {
  email: string;
  password: string;
};

export const LoginPage = () => {
  useDocumentTitle("Log in");

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const token = searchParams.get("token") ?? "";

  const [login, loginResult] = useMutationWithToast(api.useLoginMutation(), {
    errorMessage: "The email or password you entered is incorrect.",
    onSuccess: () => {
      navigate(
        (location.state as { returnTo?: string } | null)?.returnTo ?? "/",
        { replace: true },
      );
    },
  });
  const { handleSubmit, control } = useForm<LoginForm>({
    defaultValues: { email, password: "" },
  });
  const onSubmit = handleSubmit(({ email, password }) => {
    login({ email, password, invite_token: token });
  });

  return (
    <div className="flex size-full flex-col">
      <Form
        className="flex flex-auto flex-col justify-center"
        validationBehavior="aria"
        onSubmit={onSubmit}
      >
        <img
          src={logo}
          alt="logo"
          className="mb-5xl h-5xl md:mb-7xl md:h-7xl"
        />
        <Controller
          control={control}
          name="email"
          rules={emailFieldRules}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              type="email"
              label="Email"
              placeholder="Enter your email"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="password"
          rules={{ required: "Password is required" }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              className="mt-2xl"
              type="password"
              label="Password"
              placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
          )}
        />
        <Button
          className="mt-3xl mb-lg w-full"
          type="submit"
          size="lg"
          isLoading={loginResult.isLoading}
        >
          Log in
        </Button>
        {token ? (
          <Button
            className="w-full"
            color="tertiary"
            size="lg"
            href={`/accept-invite?${searchParams}`}
          >
            Don't have an account? Sign up
          </Button>
        ) : (
          <Button
            className="w-full"
            color="tertiary"
            size="lg"
            href={`/forgot-password?${searchParams}`}
          >
            Reset password
          </Button>
        )}
      </Form>
      {!token && (
        <div className="flex-none py-4xl text-center text-md text-tertiary">
          <span>New to Light? </span>
          <a
            className="underline"
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.poweredbylight.com"
          >
            Learn more
          </a>
        </div>
      )}
    </div>
  );
};
